import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { RecurringTransactionService, UpcomingRecurringCard } from '../../services/recurring-transaction.service';

@Component({
  selector: 'app-upcoming-recurring-cards',
  imports: [DatePipe, RouterLink],
  templateUrl: './upcoming-recurring-cards.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpcomingRecurringCardsComponent {
  private readonly recurringTransactionService = inject(RecurringTransactionService);

  readonly upcomingCardsQuery = injectQuery(() => this.recurringTransactionService.upcomingCardsQueryOptions());
  readonly upcomingCards = computed(() => this.upcomingCardsQuery.data() ?? []);
  readonly showUpcomingSection = computed(() => !this.upcomingCardsQuery.isError() && this.upcomingCards().length > 0);

  isToday(card: UpcomingRecurringCard): boolean {
    return card.daysUntilExpected === 0;
  }

  isIncome(card: UpcomingRecurringCard): boolean {
    return card.direction === 'INCOME';
  }

  amountLabel(card: UpcomingRecurringCard): string | null {
    if (card.amountRange !== null)
      return `${this.formatAmount(card.amountRange.min)}-${this.formatAmount(card.amountRange.max).replace('$', '')}`;

    if (card.amount === null) return null;

    const formatted = this.formatAmount(card.amount);

    return card.direction === 'INCOME' ? `+${formatted}` : formatted;
  }

  private formatAmount(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  expectedInLabel(card: UpcomingRecurringCard): string {
    if (card.daysUntilExpected === 0) return 'today';
    if (card.daysUntilExpected === 1) return 'in 1 day';
    if (card.daysUntilExpected === -1) return '1 day ago';
    if (card.daysUntilExpected < 0) return `${Math.abs(card.daysUntilExpected)} days ago`;
    return `in ${card.daysUntilExpected} days`;
  }
}
